import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// A thermosiphon solar water heater: a tilted flat-plate collector with a glass
// cover over a dark absorber, feeding a horizontal storage tank along its top
// edge, all on a steel frame. Solar heat, not photovoltaics.
const ABSORB = "#26303a";
const GLASS = "#5a7e92";
const FRAME = "#b8bcc4";
const TANK = "#d6d9dc";
const TANK_END = "#aab0b6";
const PIPE = "#b87333";
const LEG = "#6b7079";

export const solarWaterHeater = defineModel({
  id: "solar-water-heater",
  batchId: "electric",
  name: "Solar water heater",
  tags: ["prop", "electric", "power", "solar", "thermal", "renewable", "water"],
  summary: "A tilted thermal collector feeding a horizontal storage tank.",
  footprint: 0.4,
  parts(): Part[] {
    const tilt = 0.6;
    const cx = 0, cy = 0.34, cz = 0.02;
    const c = Math.cos(tilt), s = Math.sin(tilt);
    const P = (lx: number, ly: number, lz: number): Vec3 => [cx + lx, cy + ly * c - lz * s, cz + ly * s + lz * c];
    const rot: Vec3 = [tilt, 0, 0];
    return [
      // Frame legs.
      { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.36, 8), color: LEG, position: [-0.26, 0.18, -0.12] },
      { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.36, 8), color: LEG, position: [0.26, 0.18, -0.12] },
      { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.16, 8), color: LEG, position: [-0.26, 0.1, 0.16] },
      { geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.16, 8), color: LEG, position: [0.26, 0.1, 0.16] },
      // Collector: frame, absorber, glass cover.
      { geometry: new THREE.BoxGeometry(0.62, 0.04, 0.5), color: FRAME, position: P(0, 0, 0), rotation: rot },
      { geometry: new THREE.BoxGeometry(0.56, 0.02, 0.44), color: ABSORB, position: P(0, 0.02, 0), rotation: rot },
      { geometry: new THREE.BoxGeometry(0.56, 0.015, 0.44), color: GLASS, position: P(0, 0.035, 0), rotation: rot },
      // Storage tank seated along the collector's top edge (the high, -Z, side),
      // matched to the collector width so it does not overhang.
      { geometry: new THREE.CylinderGeometry(0.095, 0.095, 0.56, 14), color: TANK, position: [0, 0.55, -0.19], rotation: [0, 0, Math.PI / 2] },
      { geometry: new THREE.CylinderGeometry(0.1, 0.1, 0.03, 14), color: TANK_END, position: [-0.28, 0.55, -0.19], rotation: [0, 0, Math.PI / 2] },
      { geometry: new THREE.CylinderGeometry(0.1, 0.1, 0.03, 14), color: TANK_END, position: [0.28, 0.55, -0.19], rotation: [0, 0, Math.PI / 2] },
      // Short riser pipes joining the collector's top edge to the tank.
      { geometry: new THREE.CylinderGeometry(0.016, 0.016, 0.12, 6), color: PIPE, position: [-0.22, 0.5, -0.19] },
      { geometry: new THREE.CylinderGeometry(0.016, 0.016, 0.12, 6), color: PIPE, position: [0.22, 0.5, -0.19] },
    ];
  },
  animations: [],
});
